const API_BASE_URL = 'https://apisaleeasy.psisat.com';

export const salesTeamService = {
  // Get teams with members
  async getTeamsWithMembers() {
    try {
      const url = `${API_BASE_URL}/api/sales-team/teams-with-members`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch sales teams data');
      }
    
    } catch (error) {
      console.error('Error fetching sales teams data:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // Format date for API (YYYY-MM-DD format) — use local date parts to avoid UTC timezone shift
  formatDateForAPI(date) {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  },

  // Get default date range (current year)
  getDefaultDateRange() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(0, 1); // January 1st of current year
    endDate.setMonth(11, 30); // December 30th of current year
    
    return {
      start_date: this.formatDateForAPI(startDate),
      end_date: this.formatDateForAPI(endDate),
      group_by: "month"
    };
  },

  // Get default filters
  getDefaultFilters() {
    return {
      company_code: "045"
    };
  },

  // Get default filters for MAQ (company code 043)
  getMaqDefaultFilters() {
    return {
      company_code: "043"
    };
  },

  // Get default filters for RAM (company code 012)
  getRamDefaultFilters() {
    return {
      company_code: "012"
    };
  },

  // Get default filters for PsiCorp (company code 002)
  getPsicorpDefaultFilters() {
    return {
      company_code: "002"
    };
  },

  // Get default request payload
  getDefaultRequest() {
    return {
      salesman_code: "B087",
      date_range: this.getDefaultDateRange(),
      filters: this.getDefaultFilters()
    };
  },

  // Get team sales summary for all teams
  async getTeamSalesSummary(startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate)
        },
        filters: this.getDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('Team sales summary raw API response:', data);
      console.log('Team sales summary API response structure:', JSON.stringify(data, null, 2));
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch team sales summary');
      }
    
    } catch (error) {
      console.error('Error fetching team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get team sales summary for specific team
  async getSpecificTeamSalesSummary(teamId, startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate)
        },
        team_id: teamId,
        filters: this.getDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch specific team sales summary');
      }
    
    } catch (error) {
      console.error('Error fetching specific team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Format number to Thai format
  formatThaiNumber(number) {
    if (!number && number !== 0) return '0.00';
    return Number(number).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  },

  // Format integer number (no decimals)
  formatThaiInteger(number) {
    if (!number && number !== 0) return '0';
    return Number(number).toLocaleString('th-TH');
  },

  // Get current month date range
  getCurrentMonthDateRange() {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date();
    
    return {
      start_date: this.formatDateForAPI(startDate),
      end_date: this.formatDateForAPI(endDate)
    };
  },

  // Convert amount to before VAT (remove 7% VAT)
  convertToBeforeVAT(amount) {
    if (!amount || amount === 0) return 0;
    // Amount with VAT / 1.07 = Amount before VAT
    // Keep more precision during calculation, round only at the end
    return amount / 1.07;
  },

  // Get salesman analytics (step 1: get invoice list)
  async getSalesmanAnalytics(salesmanCode, startDate, endDate, companyCode = '045') {
    try {
      const url = `${API_BASE_URL}/api/sales-analytics/salesman`;

      const requestBody = {
        salesman_code: salesmanCode,
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate),
          group_by: "month"
        },
        filters: { company_code: companyCode }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch salesman analytics');
      }
    
    } catch (error) {
      console.error('Error fetching salesman analytics:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get product category summary for a single invoice
  async getInvoiceProductCategorySummary(invoiceNumber, companyCode = '045') {
    try {
      const url = `${API_BASE_URL}/api/inv/product-category-summary`;

      const requestBody = {
        Inv_Number: invoiceNumber.trim(),
        CompanyCode: companyCode
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch product category summary');
      }
    
    } catch (error) {
      console.error('Error fetching product category summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get MAQ team sales summary for all teams
  async getMaqTeamSalesSummary(startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team-maq/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate),
          group_by: "month"
        },
        filters: this.getMaqDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('MAQ team sales summary raw API response:', data);
      console.log('MAQ team sales summary API response structure:', JSON.stringify(data, null, 2));
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch MAQ team sales summary');
      }
    
    } catch (error) {
      console.error('Error fetching MAQ team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get MAQ team sales summary for specific team
  async getSpecificMaqTeamSalesSummary(teamId, startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team-maq/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate),
          group_by: "month"
        },
        team_id: teamId,
        filters: this.getMaqDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch specific MAQ team sales summary');
      }
    
    } catch (error) {
      console.error('Error fetching specific MAQ team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get RAM team sales summary for all teams
  async getRamTeamSalesSummary(startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team-ram/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate)
        },
        filters: this.getRamDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('RAM team sales summary raw API response:', data);
      console.log('RAM team sales summary API response structure:', JSON.stringify(data, null, 2));
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch RAM team sales summary');
      }
    
    } catch (error) {
      console.error('Error fetching RAM team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get RAM team sales summary for specific team
  async getSpecificRamTeamSalesSummary(teamId, startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team-ram/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate)
        },
        team_id: teamId,
        filters: this.getRamDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch specific RAM team sales summary');
      }
    
    } catch (error) {
      console.error('Error fetching specific RAM team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get PsiCorp team sales summary for all teams
  async getPsicorpTeamSalesSummary(startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team-psicorp/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate),
          group_by: "month"
        },
        filters: this.getPsicorpDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('PsiCorp team sales summary raw API response:', data);

      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch PsiCorp team sales summary');
      }

    } catch (error) {
      console.error('Error fetching PsiCorp team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get PsiCorp team sales summary for specific team
  async getSpecificPsicorpTeamSalesSummary(teamId, startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team-psicorp/team-sales-summary`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate),
          group_by: "month"
        },
        team_id: teamId,
        filters: this.getPsicorpDefaultFilters()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch specific PsiCorp team sales summary');
      }

    } catch (error) {
      console.error('Error fetching specific PsiCorp team sales summary:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get product sales detail for specific salesman
  async getProductSalesDetail(salesmanCode, startDate, endDate, companyCode = "045") {
    try {
      const url = `${API_BASE_URL}/api/sales-team/product-sales-detail`;

      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate)
        },
        salesman_code: salesmanCode,
        filters: {
          company_code: companyCode,
          item_number: ""
        }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch product sales detail');
      }
    
    } catch (error) {
      console.error('Error fetching product sales detail:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Get factory type for a product
  async getFactoryType(itemNumber) {
    try {
      const url = `${API_BASE_URL}/api/factory/type`;

      const requestBody = {
        num_name_products: itemNumber
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status) {
        return {
          success: true,
          data: data.data,
          result_code: data.result_code,
          result_desc: data.result_desc
        };
      } else {
        throw new Error(data.result_desc || 'Failed to fetch factory type');
      }
    
    } catch (error) {
      console.error('Error fetching factory type:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  // Search all credit notes for a date range (all pages)
  async searchAllCreditNotes(startDate, endDate, companyCode = '045') {
    try {
      const allCNs = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(`${API_BASE_URL}/api/cn/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            DateFrom: this.formatDateForAPI(startDate),
            DateTo: this.formatDateForAPI(endDate),
            CompanyCode: companyCode,
            page,
            limit: 50
          })
        });

        if (!response.ok) break;
        const data = await response.json();
        const cns = data?.data?.credit_notes || [];
        allCNs.push(...cns);

        const pagination = data?.data?.pagination || {};
        hasMore = page < (pagination.pages || 1);
        page++;
      }

      return allCNs;
    } catch (error) {
      console.error('Error searching credit notes:', error);
      return [];
    }
  },

  // Get CN category summary from new API
  async getCnCategorySummary(cnNumber, companyCode = '045') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cn/category-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cn_number: cnNumber, company_code: companyCode })
      });

      if (!response.ok) return null;
      const data = await response.json();
      if (data.status && data.data) return data.data;
      return null;
    } catch (error) {
      console.error(`Error fetching CN category summary for ${cnNumber}:`, error);
      return null;
    }
  },

  // Export monthly team sales data for Excel
  async exportTeamMonthly(startDate, endDate) {
    try {
      const url = `${API_BASE_URL}/api/sales-team/export-team-monthly`;
      const requestBody = {
        date_range: {
          start_date: this.formatDateForAPI(startDate),
          end_date: this.formatDateForAPI(endDate)
        },
        filters: this.getDefaultFilters()
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.status) return { success: true, data: data.data };
      throw new Error(data.result_desc || 'Failed to fetch monthly export data');
    } catch (error) {
      console.error('Error fetching monthly export data:', error);
      return { success: false, error: error.message, data: null };
    }
  },

  // Get combined category analytics for a salesman (both steps combined)
  async getSalesmanCategoryAnalytics(salesmanCode, startDate, endDate, actualMemberAmount = null, customerBreakdownByType = null) {
    try {
      const categorySummary = { SAT: 0, Solar: 0, MTF: 0, MTB: 0, Other: 0 };
      let processedInvoices = 0;
      let totalInvoices = 0;

      // Query all 3 companies
      for (const companyCode of ['045', '043', '012']) {
        const analyticsResponse = await this.getSalesmanAnalytics(salesmanCode, startDate, endDate, companyCode);
        if (!analyticsResponse.success) continue;

        const invoices = analyticsResponse.data?.invoices || [];
        totalInvoices += invoices.length;
        if (invoices.length === 0) continue;

        // Get category summary for each invoice
        const maxConcurrentRequests = 5;
        for (let i = 0; i < invoices.length; i += maxConcurrentRequests) {
          const batch = invoices.slice(i, i + maxConcurrentRequests);
          const batchResults = await Promise.allSettled(
            batch.map(invoice => this.getInvoiceProductCategorySummary(invoice.invoice_number, companyCode))
          );
          batchResults.forEach(result => {
            if (result.status === 'fulfilled' && result.value.success) {
              const categoryData = result.value.data?.summary_by_category || {};
              Object.entries(categoryData).forEach(([category, amount]) => {
                const amountBeforeVAT = Math.round(this.convertToBeforeVAT(Number(amount) || 0) * 100) / 100;
                const target = categorySummary.hasOwnProperty(category) ? category : 'Other';
                categorySummary[target] += amountBeforeVAT;
              });
              processedInvoices++;
            }
          });
        }

        // Deduct CN for this company
        const allCNs = await this.searchAllCreditNotes(startDate, endDate, companyCode).catch(() => []);
        const salesmanCNs = allCNs.filter(cn => (cn.SalesmanCode || '').trim() === salesmanCode);
        const cnBatchSize = 5;
        for (let i = 0; i < salesmanCNs.length; i += cnBatchSize) {
          const batch = salesmanCNs.slice(i, i + cnBatchSize);
          const batchResults = await Promise.allSettled(
            batch.map(cn => this.getCnCategorySummary((cn.CN_Number || '').trim()))
          );
          batchResults.forEach(result => {
            if (result.status === 'fulfilled' && result.value) {
              const cnData = result.value;
              const cnAmount = cnData.cn_amount || 0;
              const invoiceCategoryAmounts = cnData.invoice_category_amounts || {};
              const invoiceCategoryTotal = Object.values(invoiceCategoryAmounts)
                .reduce((sum, amt) => sum + (Number(amt) || 0), 0);
              if (invoiceCategoryTotal > 0) {
                Object.entries(invoiceCategoryAmounts).forEach(([cat, amt]) => {
                  const proportion = (Number(amt) || 0) / invoiceCategoryTotal;
                  const cnDeduction = Math.round(this.convertToBeforeVAT(cnAmount * proportion) * 100) / 100;
                  const target = categorySummary.hasOwnProperty(cat) ? cat : 'Other';
                  categorySummary[target] -= cnDeduction;
                });
              }
            }
          });
        }
      }

      // Floor at 0 (prevent CN over-deduction from making categories negative)
      ['SAT', 'Solar', 'MTF', 'MTB', 'Other'].forEach(k => {
        if (categorySummary[k] < 0) categorySummary[k] = 0;
      });

      if (totalInvoices === 0) {
        return { success: true, data: { summary: { SAT: 0, Solar: 0, MTF: 0, MTB: 0, Other: 0 }, total: 0, invoiceCount: 0, totalInvoices: 0 } };
      }

      // Round each category to 2 decimal places, maintain order
      const roundedCategorySummary = {};
      ['SAT', 'Solar', 'MTF', 'MTB', 'Other'].forEach(category => {
        roundedCategorySummary[category] = Math.round((categorySummary[category] || 0) * 100) / 100;
      });

      // Calculate total from rounded amounts
      const calculatedTotal = Object.values(roundedCategorySummary).reduce((sum, amount) => sum + amount, 0);
      let finalTotal = Math.round(calculatedTotal * 100) / 100;

      return {
        success: true,
        data: {
          summary: roundedCategorySummary,
          total: Math.round(finalTotal * 100) / 100,
          invoiceCount: processedInvoices,
          totalInvoices: totalInvoices
        }
      };

    } catch (error) {
      console.error('Error getting salesman category analytics:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }
};

export default salesTeamService;